/**
 * Maps an aircraft `iconKind` to a 3D GLB model exported from Blender, and
 * provides the per-kind tint color used by the custom 3D layer. The GLB files
 * live in `apps/web/public/models/aircraft/` and are exported at a 1000 m
 * wingspan so they match the hologram silhouette footprint exactly.
 *
 * Color values mirror `ICON_DEFS` / `AIRCRAFT_COLOR_EXPR` so a 3D model reads
 * as the same aircraft "kind" as the ground SVG icon and the colored pillar.
 */

import type { AircraftIconKind } from '@flightscanner/shared';

const MODEL_BASE = '/models/aircraft/';

/** iconKind -> GLB filename. Models are shared across kinds with the same shape. */
const MODEL_FILES: Record<AircraftIconKind, string> = {
  presidential: 'presidential.glb',
  government: 'government.glb',
  emergency: 'commercial.glb',
  'military-fighter': 'military-fighter.glb',
  'military-transport': 'military-transport.glb',
  'military-other': 'military-other.glb',
  'military-helicopter': 'military-helicopter.glb',
  helicopter: 'helicopter.glb',
  balloon: 'balloon.glb',
  'heavy-commercial': 'heavy-commercial.glb',
  commercial: 'commercial.glb',
  regional: 'regional.glb',
  turboprop: 'turboprop.glb',
  'private-jet': 'private-jet.glb',
  'light-prop': 'light-prop.glb',
  glider: 'glider.glb',
  unknown: 'unknown.glb',
};

/** Tint color per iconKind, matching AIRCRAFT_COLOR_EXPR. */
const MODEL_COLORS: Record<AircraftIconKind, string> = {
  presidential: '#fde047',
  government: '#fbbf24',
  emergency: '#ef4444',
  'military-fighter': '#fb923c',
  'military-transport': '#f97316',
  'military-other': '#fdba74',
  'military-helicopter': '#f97316',
  helicopter: '#22d3ee',
  balloon: '#f472b6',
  'heavy-commercial': '#38bdf8',
  commercial: '#60a5fa',
  regional: '#7dd3fc',
  turboprop: '#67e8f9',
  'private-jet': '#c4b5fd',
  'light-prop': '#a7f3d0',
  glider: '#bbf7d0',
  unknown: '#cbd5e1',
};

/** Presidential livery is preserved (multi-color) rather than tinted. */
const PRESERVE_COLOR = new Set<AircraftIconKind>(['presidential']);

export interface AircraftModelDef {
  url: string;
  color: string;
  preserveColor: boolean;
}

export function getAircraftModelDef(
  iconKind: AircraftIconKind,
  emergency: boolean,
): AircraftModelDef {
  const file = MODEL_FILES[iconKind] ?? MODEL_FILES.unknown;
  const baseColor = MODEL_COLORS[iconKind] ?? MODEL_COLORS.unknown;
  const isPresidential = PRESERVE_COLOR.has(iconKind);
  return {
    url: `${MODEL_BASE}${file}`,
    color: emergency && !isPresidential ? '#ef4444' : baseColor,
    preserveColor: isPresidential,
  };
}

/** Distinct GLB URLs that need to be loaded (8 unique shapes for 16 kinds). */
export function allModelUrls(): string[] {
  return Array.from(new Set(Object.values(MODEL_FILES))).map((f) => `${MODEL_BASE}${f}`);
}
